/**
 * Enhanced Shared Cart Service
 * 
 * Improved version with better error handling, performance optimizations
 * 
 * ## ✅ Key Features:
    * Always clears cart first, then rebuilds fully from metafield JSON data that save in local storage.
    * Retrieve cart data from the previously saved local storage.
    * Call the API to fetch the latest, user-specific variant prices from the variant metafields.
    * Save the updated JSON (with the latest prices) back into local storage.
    * 
    * **Checkout lock:
    * If `is_checkout_started` is `true`, rebuild is skipped.
    * Lock message is shown.
    * Prevents overwriting the cart mid-checkout.
    * 
    * **Sync runs:
    * 
    * On `cart update` (origin device → save in local storage → save to metafield).
    * On `login` (other devices → rebuild from metafield JSON data save in local storage).
    * On `page load` (if logged in).
    * On `checkout start` (sets lock in metafield).
    * On `checkout finished` (clear shop metafiled and local storage).
    * 
    * NOTE:
    * handleCartChange(); method call in this file (checkout-cart-manager.js) for detecting every cart event. i.e. add, update, change, etc.
    * localStorage.setItem('current_window_user_changed','0'); set this code in this file (custom.js) to set the flag when current user changing progress
    * localStorage.removeItem('shared_cart_json') set this code in this file (custom.js) to remove the shared cart json when current user changing progress
 * @author Dynamic Dreamz Development Team
 * @version 1.0
 */


class SharedCart {
  constructor() {
    this.localKey = `shared_cart_json`;
    this.shopApiUrl = `/apps/stroetmann/api/v1/users/set_user_cart`;
    this.getVariantPriceUrl = `/apps/stroetmann/api/v1/users/get_variant_price`;
    this.lockMessage = `Order in progress. You cannot add or modify products to the cart.`;
    this.sc_timestamp = Date.now();
    this.isUserChanged = localStorage.getItem('current_window_user_changed');
  }

  /** Get current Shopify cart JSON */
  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }

  /** Extract only required fields: attributes + items (id, qty, properties) */
  sanitizeCart(cart) {
    return {
      customerId: cart.attributes['customer_id'] || window.customer?.id || '',
      userName: cart.attributes['user_name'] || window.customer?.userName || '',
      userNumber: cart.attributes['current_user'] || window.customer?.currentUserNumber || '',
      is_checkout_started: cart.attributes['draft_order_lock_reason'] == 'checkout_started' ? true : false,
      // is_checkout_started: true,
      current_delivery_date: cart.attributes['current_date'] || window.customer?.selectedDeliveryDate || '',
      token: cart.token,
      timestamp: this.sc_timestamp,
      items: Array.isArray(cart.items) && cart.items.length > 0 ?
        cart.items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          properties: item.properties || {},
          handle: item.handle,
        })) :
        []
    };
  }

  /** Save sanitized cart JSON to localStorage */
  async saveCartLocally(cart) {
    const sanitized = this.sanitizeCart(cart);

    const metafieldCart = this.getCartFromLocalStorage();

    // 🔒 Prevent sync if checkout is started from other device
    if (metafieldCart && metafieldCart.is_checkout_started && cart.token !== metafieldCart.token) {
      return;
    }

    localStorage.setItem(this.localKey, JSON.stringify(sanitized));
  }

  /** Push sanitized cart JSON to backend metafield */
  async saveCartToMetafield(cart) {

    const metafieldCart = this.getCartFromLocalStorage();
    if (!metafieldCart) return;

    // check if user number change is in progress
    // if is in progress then we do not save cart to the metafield as 
    // user number change involve cart clear function so in this case we loose our current cart json for current user number
    // so when changing the user number we simply return from here and not save the cart json to metafield
    console.log(`🔄 Skip cart save to metafield if isUserChanged == 0,  Now ==`,this.isUserChanged);
    if(this.isUserChanged === '0'){
      return;
    }

    // 🔒 Prevent sync if checkout is started from other device
    if (metafieldCart.is_checkout_started && cart.token !== metafieldCart.token) {
      console.log('🔒 Checkout in progress from other user → skipping sync to metafileds');
      this.showCartLockMessage();
      return;
    }

    // Compare tokens and current user number → only save to metafield if matched
    if (cart.token !== metafieldCart.token || window.customer?.currentUserNumber !== metafieldCart.userNumber) {
      console.log('❌ Tokens not matched → no save needed to metafield at this time');
      return;
    }

    try {
      const sanitized = this.sanitizeCart(cart);
      console.log(`sanitized cart >>`, sanitized);
      await fetch(this.shopApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitized),
      });
      console.log('✅ Sanitized cart saved to metafield');
    } catch (err) {
      console.error('❌ Error saving cart to metafield:', err);
    }
  }

  /** Get metafield cart JSON from localStorage (no API fetch) */
  getCartFromLocalStorage() {
    const data = localStorage.getItem(this.localKey);
    return data ? JSON.parse(data) : null;
  }

  /** Rebuild local Shopify cart from metafield JSON (clear + batch add) */
  async rebuildCart(metafieldCart) {

    if (metafieldCart?.items?.length === 0) {
      console.log('ℹ️ No items in metafield cart json → clearing local cart');
      await fetch('/cart/clear.js', { method: 'POST' });

      /**
       * Refresh cart items section
       * Called from other components like product-form and cart-drawer
      */
      setTimeout(() => {
        if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            cartItems.refreshCartItems();
          }
        }
      }, 200);
      return;
    }

    console.log(`➕ Rebuilt cart started`);

    try {
      // 1. Clear current cart
      await fetch('/cart/clear.js', { method: 'POST' });
      console.log('🗑️ Cleared current cart');

      // 2. Add all metafield items in one request
      const itemsToAdd = metafieldCart.items.map(item => ({
        id: item.id,
        quantity: item.quantity,
        properties: item.properties || {},
      }));

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd }),
      });

      console.log(`➕ Rebuilt cart with ${itemsToAdd.length} items from metafield`);
      
      /**
       * Refresh cart items section
       * Called from other components like product-form and cart-drawer
      */
      setTimeout(async () => {
        if (window.location.pathname === theme.routes.cart) {
          if(window.checkout && window.checkout.render){
            await window.checkout.render();
          }else{
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              cartItems.refreshCartItems();
            }
          }
        }
      }, 100);

    } catch (err) {
      console.error('❌ Error rebuilding cart:', err);
    }
  }

  /** Fetch the latest, user-specific variant prices from the variant metafields */
  async fetchLatestPricesFromMeta(cart) {

    const metafieldCart = this.getCartFromLocalStorage();
    if (!metafieldCart) return;

    const itemIds = metafieldCart.items.map(item => item.id);

    if(!itemIds.length) return;

    const payload = {
      "customerId": `${window.customer?.id}`,
      "userName": `${window.customer?.userName}`,
      "userNumber": `${cart.attributes['current_user']}` || `${window.customer?.currentUserNumber}`,
      "currentUser": `${cart.attributes['current_user']}` || `${window.customer?.currentUserNumber}`,
      "productVariantIds": itemIds
    };

    if(!payload.currentUser) return;

    try {
      const response = await fetch(this.getVariantPriceUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('⏳ Fetch latest prices from variant metafield');

      if (data?.price?.length) {
        const priceMap = Object.fromEntries(data.price.map(p => [p.id, p.price]));

        let isUpdated = false;
        const updatedItems = metafieldCart.items.map(item => {
          const newPrice = (priceMap[item.id] * 100).toFixed(0);
          if (newPrice && item.properties?._price != newPrice) {
            isUpdated = true;
            return {
              ...item,
              properties: {
                ...item.properties,
                _price: (newPrice*1)
              }
            };
          }
          return item;
        });

        // Only update localStorage if something actually changed
        if (isUpdated) {
          metafieldCart.items = updatedItems;
          metafieldCart.token = `${metafieldCart.is_checkout_started ? metafieldCart.token : `${metafieldCart.token}+priceUpdated`}`; //update token so that cart rebuild again with new prices
          localStorage.setItem('shared_cart_json', JSON.stringify(metafieldCart));
          console.log('✅ Local storage updated with latest prices');
        } else {
          console.log('ℹ️ No price changes detected, local storage not updated');
        }
      }

    } catch (err) {
      console.error('❌ Error fetching latest prices from variant metafield:', err);
    }
  }

  /** Sync cart from localStorage metafield JSON */
  async syncFromLocalStorage() {

    const currentCart = await this.getCart();
    await this.fetchLatestPricesFromMeta(currentCart);

    const metafieldCart = this.getCartFromLocalStorage();
    if (!metafieldCart) return;

    const cartCountEle = document.getElementById(`cart-icon-bubble`);
    const emptyCartBtnEle = document.querySelector(`cart-checkout .js-cart-empty a.btn`);

    // 🔒 Prevent sync if checkout is locked
    if (metafieldCart.is_checkout_started && currentCart.token !== metafieldCart.token) {
      console.log('🔒 Checkout in progress from other user → skipping sync from local storage');
      this.showCartLockMessage();
      return;
    }

    console.log(`syncFromLocalStorage, isUserChanged = 1 OR null >>`,this.isUserChanged);
    // Compare tokens and current user number → only rebuild if different
    // rebuild also if changing user location id from header dropdown
    //console.log(`currentCart.token >>`,currentCart.token, `metafieldCart.token >>`,metafieldCart.token, `customer?.currentUserNumber >>`,customer?.currentUserNumber, `metafieldCart.userNumber >>`, metafieldCart.userNumber);
    if (this.isUserChanged === '1'  || (currentCart.token !== metafieldCart.token || window.customer?.currentUserNumber !== metafieldCart.userNumber)) {
      console.log('🔄 Tokens or currentUserNumber defer → rebuilding cart');
      
      if(cartCountEle){
        cartCountEle.classList.add(`loading`);
        cartCountEle.querySelector(`.cart-icon-bubble-loading`)?.classList.remove(`hidden`);
      }
      if(emptyCartBtnEle) emptyCartBtnEle.classList.add('btn--loading');

      await this.rebuildCart(metafieldCart);
      localStorage.removeItem('current_window_user_changed');

      setTimeout(() => {
        if(cartCountEle){
          cartCountEle.classList.remove(`loading`);
          cartCountEle.querySelector(`.cart-icon-bubble-loading`)?.classList.add(`hidden`);
        }
        if(emptyCartBtnEle) emptyCartBtnEle.classList.remove('btn--loading');
      },750);

    } else {
      console.log('✅ Tokens or currentUserNumber match → no rebuild needed');
    }
  }

  /** Handle local cart change */
  /* called this function on outside file (checkout-cart-manager.js) for detect every cart event once cart event detected we call this function */
  async handleCartChange() {
    const cart = await this.getCart();
    await this.saveCartLocally(cart);   // save to localStorage
    await this.saveCartToMetafield(cart); // also save to backend
  }

  /** Lock cart when checkout starts */
  async lockCartOnCheckout() {
    const cart = await this.getCart();
    await this.saveCartToMetafield(cart); // mark checkout as in progress
    // this.showCartLockMessage();
  }

  /** Show lock message on cart page */
  showCartLockMessage() {
    const cartContainer = document.querySelector('#cart__container');

    if (cartContainer) {
      cartContainer.setAttribute(`hidden`,``);
      let lockNotice = document.querySelector('#cart-lock-message');
      if (!lockNotice) {
        lockNotice = document.createElement('div');
        lockNotice.id = 'cart-lock-message';
        lockNotice.style.cssText = `color: red; font-weight: bold; text-align: center; font-size: 2rem;`;
        cartContainer.parentElement.prepend(lockNotice);
      }
      lockNotice.innerText = this.lockMessage;
    }

    //hide csv import/export section
    const cartImortCsvContainer = document.querySelector('.cc-csv-import');
    if(cartImortCsvContainer){
      cartImortCsvContainer.setAttribute(`hidden`,``);
    }
  }

  /** Init listeners */
  init() {

    // Listen for checkout completion
    document.addEventListener('checkout:completed', async (event) => {
      console.log('✅ Checkout completed → re synsc metafield >>');
      await this.handleCartChange();
    });

    // Run on checkout start
    document.addEventListener('checkout:started', async () => {
      console.log('🔒 Checkout started → locking cart >>');
      await this.lockCartOnCheckout();
    });

    // check once on page load (for logged-in customers)
    if (window.customer) {
      this.syncFromLocalStorage();
    }
  
  }
}

const sharedCart = new SharedCart();
sharedCart.init();
